import React, { useEffect, useState, useRef, Suspense, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  FlatList,
  Image,
  Dimensions,
  Modal,
  SafeAreaView,
  Alert,
  TextInput,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import { useLotteryStore } from '../../stores';
import { getLatestLottery } from '../../api';
import { COLORS, LOTTERY_TYPES, LOTTERY_NAMES, API_BASE_URL } from '../../utils/constants';

// 懒加载组件
const ToolModal = React.lazy(() => import('./components/ToolModal'));
// BbsModal 已移除
const QueryModal = React.lazy(() => import('./components/QueryModal'));
const StatsModal = React.lazy(() => import('./components/StatsModal'));
const YmccModal = React.lazy(() => import('./components/YmccModal'));
const SearchModal = React.lazy(() => import('./components/SearchModal'));
const CorpusModal = React.lazy(() => import('./components/CorpusModal'));

const { width } = Dimensions.get('window');

// 渐进加载图片组件（Web 兼容版本，使用 CSS transition）
const ProgressiveImage = React.memo(({ uri, style }: { uri: string; style: any }) => {
  const [loaded, setLoaded] = useState(false);

  const onLoad = useCallback(() => {
    setLoaded(true);
  }, []);

  return (
    <View style={[style, { backgroundColor: '#e9e9e9', overflow: 'hidden' }]}>
      {!loaded && (
        <View style={[StyleSheet.absoluteFill, { justifyContent: 'center', alignItems: 'center', backgroundColor: '#f0f0f0' }]}>
          <Text style={{ color: '#ccc', fontSize: 24, opacity: 0.5 }}>🖼️</Text>
        </View>
      )}
      <Image
        source={{ uri }}
        style={[style, { opacity: loaded ? 1 : 0, transition: 'opacity 0.3s ease' } as any]}
        resizeMode="cover"
        onLoad={onLoad}
      />
    </View>
  );
});

// 分享配置类型
interface ShareConfig {
  qrcode_url: string;
  display_url: string;
  share_title: string;
}

export default function HomeSimple() {
  const { currentType, setLotteryType } = useLotteryStore();
  const [lotteryInfo, setLotteryInfo] = useState<any>(null);
  const [pictures, setPictures] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(2026);
  const [color, setColor] = useState(1); // 1=彩图, 2=黑白
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [shareConfig, setShareConfig] = useState<ShareConfig>({
    qrcode_url: 'http://10963.com',
    display_url: '10963.com',
    share_title: '桐伴图库'
  });
  const [showShareModal, setShowShareModal] = useState(false);
  const [showLiveModal, setShowLiveModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [videoLoading, setVideoLoading] = useState(false);
  const shareRef = useRef<View>(null);
  
  // 资料大全弹窗状态
  const [showCorpusModal, setShowCorpusModal] = useState(false);

  // 搜索弹窗状态
  const [showSearchModal, setShowSearchModal] = useState(false);

  // 弹窗显示状态
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showYmccModal, setShowYmccModal] = useState(false);
  // showBbsModal 已移除
  const [showToolModal, setShowToolModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // 下拉刷新状态
  const [refreshing, setRefreshing] = useState(false);
  
  // 开奖倒计时状态
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  // 骨架屏加载状态
  const [lotteryLoading, setLotteryLoading] = useState(true);
  const [picturesLoading, setPicturesLoading] = useState(true);
  
  // 即时开奖动画状态
  const [visibleBalls, setVisibleBalls] = useState(99); // 当前显示的球数（99=全部显示）
  const [isAnimating, setIsAnimating] = useState(false); // 是否正在播放动画
  const [isWaitingDraw, setIsWaitingDraw] = useState(false); // 是否在等待开奖（倒计时结束但还没采集到结果）
  // 按彩种分别记录期号：{ 1: '2026029', 2: '2026030', 3: '2026012' }
  const lastPeriodsRef = useRef<Record<number, string>>({}); 
  
  // 判断是否在开奖时段（21:20 - 22:00）
  const isInLotteryTime = () => {
    const now = new Date();
    const hour = now.getHours();
    const minute = now.getMinutes();
    const totalMinutes = hour * 60 + minute;
    // 21:20 = 1280, 22:00 = 1320
    return totalMinutes >= 1280 && totalMinutes <= 1320;
  };

  useEffect(() => {
    loadLottery();
    loadPictures(true);
    loadShareConfig();
  }, [currentType]);

  // 自动轮询开奖数据（开奖时段高频，其他时段正常）
  useEffect(() => {
    let interval: NodeJS.Timeout;
    let lastPollTime = Date.now();
    
    // 判断是否在开奖时段（21:20 - 22:10）
    const isLotteryTime = () => {
      const now = new Date();
      const hour = now.getHours();
      const minute = now.getMinutes();
      const totalMinutes = hour * 60 + minute;
      // 21:20 = 1280, 22:10 = 1330
      return totalMinutes >= 1280 && totalMinutes <= 1330;
    };
    
    // 获取轮询间隔（开奖时段5秒，其他时段30秒）
    const getPollInterval = () => isLotteryTime() ? 5000 : 30000;
    
    const pollLottery = async () => {
      lastPollTime = Date.now();
      try {
        const res = await getLatestLottery(currentType);
        if (res.code === 10000 && res.data) {
          // 直接更新，让组件自己判断是否需要重新渲染
          setLotteryInfo((prev: any) => {
            const prevPeriod = prev?.record?.period;
            const newPeriod = res.data?.record?.period;
            if (!prev || prevPeriod !== newPeriod) {
              console.log('🎉 检测到新开奖:', newPeriod);
              return res.data;
            }
            return prev;
          });
        }
      } catch (e) {
        // 静默失败，不影响用户体验
      }
    };

    // 页面可见性变化时的处理
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // 页面变为可见时立即刷新
        console.log('📱 页面恢复可见，立即刷新开奖数据');
        pollLottery();
        // 重新设置定时器（可能时段变化了）
        clearInterval(interval);
        interval = setInterval(pollLottery, getPollInterval());
      }
    };

    // 监听页面可见性变化
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    // 立即执行一次
    pollLottery();
    
    // 动态调整轮询间隔的定时器
    const startPolling = () => {
      interval = setInterval(() => {
        pollLottery();
        // 每次轮询后检查是否需要调整间隔
        const newInterval = getPollInterval();
        // 如果当前是开奖时段但间隔是30秒，或者非开奖时段但间隔是5秒，则重新设置
        clearInterval(interval);
        interval = setInterval(pollLottery, newInterval);
      }, getPollInterval());
    };
    
    startPolling();
    
    // 每分钟检查一次是否需要切换轮询频率
    const checkInterval = setInterval(() => {
      clearInterval(interval);
      interval = setInterval(pollLottery, getPollInterval());
      if (isLotteryTime()) {
        console.log('⏰ 进入开奖时段，高频轮询中（每5秒）');
      }
    }, 60000);
    
    return () => {
      clearInterval(interval);
      clearInterval(checkInterval);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [currentType]);

  const loadShareConfig = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/config/share`);
      const data = await res.json();
      if (data.code === 1 && data.data) {
        setShareConfig(data.data);
      }
    } catch (e) {
      console.error('加载分享配置失败', e);
    }
  };

  useEffect(() => {
    loadPictures(true);
  }, [year, color]);

  // 即时开奖动画：检测到新数据时，逐个显示号码球
  // 使用 ref 记录是否正在进行动画，避免重复触发
  const animatingPeriodRef = useRef<string>('');
  
  useEffect(() => {
    const currentPeriod = lotteryInfo?.record?.period || '';
    const numberList = lotteryInfo?.record?.numberList || [];
    
    if (!currentPeriod || numberList.length === 0) return;
    
    // 获取当前彩种已记录的期号
    const lastPeriod = lastPeriodsRef.current[currentType];
    
    // 首次加载该彩种 或 切换彩种：记录期号，直接显示全部（不播放动画）
    if (!lastPeriod) {
      console.log('📋 首次加载/切换彩种，显示完整开奖结果:', currentType, currentPeriod);
      lastPeriodsRef.current[currentType] = currentPeriod;
      setVisibleBalls(99);
      setIsAnimating(false);
      setIsWaitingDraw(false);
      return;
    }
    
    // 期号变化：检测到新开奖
    if (currentPeriod !== lastPeriod) {
      console.log('🎰 检测到新开奖:', lastPeriod, '->', currentPeriod);
      lastPeriodsRef.current[currentType] = currentPeriod;
      
      // 如果正在待开奖状态，播放逐个显示动画
      // 注意：只有 isWaitingDraw 为 true 时才播放动画（即用户在页面上等待开奖）
      // 刷新页面不应该触发动画
      if (isWaitingDraw && animatingPeriodRef.current !== currentPeriod) {
        console.log('🎰 启动即时开奖动画（待开奖状态）');
        animatingPeriodRef.current = currentPeriod;
        
        setIsWaitingDraw(false);
        setVisibleBalls(0);
        setIsAnimating(true);
        
        // 每隔1.8秒显示一个球（更真实的开奖节奏）
        let count = 0;
        const interval = setInterval(() => {
          count++;
          setVisibleBalls(count);
          if (count >= numberList.length) {
            clearInterval(interval);
            setIsAnimating(false);
          }
        }, 1800);
        
        return () => clearInterval(interval);
      } else {
        // 非待开奖状态（包括刷新页面、切换回来等），直接显示全部
        console.log('📋 直接显示新结果');
        setVisibleBalls(99);
        setIsAnimating(false);
        setIsWaitingDraw(false);
      }
    }
  }, [lotteryInfo?.record?.period, currentType, isWaitingDraw]);

  // 倒计时逻辑 + 待开奖状态
  // 记录触发待开奖的期号，避免重复触发
  const waitingPeriodRef = useRef<string>('');
  
  useEffect(() => {
    if (!lotteryInfo?.nextTime) return;
    
    const currentPeriod = lotteryInfo?.record?.period || '';
    const nextPeriod = lotteryInfo?.nextLotteryNumber || '';
    
    const updateCountdown = () => {
      const nextTime = new Date(lotteryInfo.nextTime).getTime();
      const now = Date.now();
      const diff = nextTime - now;
      
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        
        // 倒计时结束后，检查数据是否已更新
        // 如果 API 返回的下期期号 > 当前显示的期号，说明新数据还没采集到
        // 此时进入待开奖状态
        const needWait = nextPeriod && currentPeriod && 
          parseInt(nextPeriod) > parseInt(currentPeriod.slice(-3)) &&
          waitingPeriodRef.current !== currentPeriod &&
          !isAnimating && !isWaitingDraw;
        
        if (needWait) {
          console.log('⏰ 倒计时结束，当前期号落后，进入待开奖状态');
          waitingPeriodRef.current = currentPeriod;
          setIsWaitingDraw(true);
          // 注意：不设置 visibleBalls(0)，让即时开奖逻辑处理
        }
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown({ days, hours, minutes, seconds });
    };
    
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [lotteryInfo?.nextTime, lotteryInfo?.record?.period, lotteryInfo?.nextLotteryNumber, isAnimating, isWaitingDraw]);

  const loadLottery = async () => {
    setLotteryLoading(true);
    try {
      const res = await getLatestLottery(currentType);
      if (res.code === 10000 && res.data) {
        setLotteryInfo(res.data);
      }
    } catch (e) {
      console.error('加载开奖失败', e);
    } finally {
      setLotteryLoading(false);
    }
  };

  const loadPictures = async (reset = false) => {
    if (loading) return;
    setLoading(true);
    if (reset) setPicturesLoading(true);
    
    const currentPage = reset ? 1 : page;
    
    try {
      const res = await fetch(`${API_BASE_URL}/article/getlist?page=${currentPage}&limit=10&lotteryType=${currentType}&year=${year}&color=${color}`);
      const data = await res.json();
      
      if (data.code === 1 && data.data.list) {
        if (reset) {
          setPictures(data.data.list);
          setPage(2);
        } else {
          setPictures([...pictures, ...data.data.list]);
          setPage(currentPage + 1);
        }
      }
    } catch (e) {
      console.error('加载图库失败', e);
    } finally {
      setLoading(false);
      setPicturesLoading(false);
    }
  };

  // 下拉刷新
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([loadLottery(), loadPictures(true)]);
    setRefreshing(false);
  }, [currentType, year, color]);

  // 固定顶部导航栏
  const renderTopNav = () => (
    <View style={styles.topNav}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{flex: 1}}>
        {[LOTTERY_TYPES.AO_CAI, LOTTERY_TYPES.XIN_AO_CAI, LOTTERY_TYPES.GANG_CAI].map((type) => (
          <TouchableOpacity
            key={type}
            style={[styles.navTab, currentType === type && styles.navTabActive]}
            onPress={() => setLotteryType(type)}
          >
            <Text style={styles.navTabText}>{LOTTERY_NAMES[type]}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      <TouchableOpacity style={styles.searchBtn} onPress={() => {
        trackAction('click_search');
        setShowSearchModal(true);
      }}>
        <Text style={styles.searchText}>搜索</Text>
      </TouchableOpacity>
    </View>
  );

  const renderHeader = () => (
    <>
      {/* 公告 */}
      <View style={styles.notice}>
        <View style={styles.noticeTag}>
          <Text style={styles.noticeTagText}>公告</Text>
        </View>
        <Text style={styles.noticeText}>欢迎使用桐伴图库！</Text>
      </View>

      {/* 开奖信息 */}
      {lotteryLoading ? (
        /* 骨架屏 */
        <View style={styles.lottery}>
          <View style={styles.skeletonTitle} />
          <View style={styles.skeletonBalls}>
            {[1,2,3,4,5,6,7].map((i) => (
              <View key={i} style={styles.skeletonBall} />
            ))}
          </View>
          <View style={styles.skeletonTime} />
        </View>
      ) : lotteryInfo && lotteryInfo.record && lotteryInfo.record.numberList?.length > 0 ? (
        <View style={styles.lottery}>
          <Text style={styles.lotteryIssue}>第 {lotteryInfo.record.period} 期开奖结果</Text>
          <View style={styles.balls}>
            {lotteryInfo.record.numberList.map((item: any, index: number) => {
              // 即时开奖效果：
              // 1. 待开奖状态：全部显示问号
              // 2. 动画中：逐个显示
              // 3. 正常状态：全部显示
              const isVisible = isWaitingDraw ? false : (index < visibleBalls);
              const isLatest = isAnimating && index === visibleBalls - 1;
              
              return (
                <View key={index} style={styles.ballItem}>
                  <View 
                    style={[
                      styles.ball, 
                      {
                        backgroundColor: isVisible 
                          ? (item.color === 1 ? '#ef4343' : item.color === 2 ? '#3e73fe' : '#07c160')
                          : '#ccc',
                      },
                      isLatest && styles.ballLatest,
                    ]}
                  >
                    <Text style={[styles.ballText, { opacity: isVisible ? 1 : 0.3 }]}>
                      {isVisible ? item.number : '?'}
                    </Text>
                  </View>
                  <Text style={[styles.ballInfo, { opacity: isVisible ? 1 : 0 }]}>
                    {isVisible ? `${item.shengXiao}/${item.wuXing}` : ''}
                  </Text>
                </View>
              );
            })}
          </View>
          {/* 状态提示 */}
          {isWaitingDraw && (
            <Text style={styles.waitingTip}>⏳ 等待开奖中，请稍候...</Text>
          )}
          {isAnimating && (
            <Text style={styles.animatingTip}>🎰 开奖中...</Text>
          )}
          {/* 倒计时显示 */}
          <View style={styles.countdownContainer}>
            <Text style={styles.countdownLabel}>距下期开奖：</Text>
            <View style={styles.countdownBox}>
              <Text style={styles.countdownNum}>{String(countdown.days).padStart(2, '0')}</Text>
              <Text style={styles.countdownUnit}>天</Text>
            </View>
            <View style={styles.countdownBox}>
              <Text style={styles.countdownNum}>{String(countdown.hours).padStart(2, '0')}</Text>
              <Text style={styles.countdownUnit}>时</Text>
            </View>
            <View style={styles.countdownBox}>
              <Text style={styles.countdownNum}>{String(countdown.minutes).padStart(2, '0')}</Text>
              <Text style={styles.countdownUnit}>分</Text>
            </View>
            <View style={styles.countdownBox}>
              <Text style={styles.countdownNum}>{String(countdown.seconds).padStart(2, '0')}</Text>
              <Text style={styles.countdownUnit}>秒</Text>
            </View>
          </View>
        </View>
      ) : (
        <View style={styles.lottery}>
          <Text style={styles.lotteryLoadingText}>暂无开奖数据</Text>
        </View>
      )}

      {/* 功能菜单 */}
      <View style={styles.menu}>
        {[
          {icon: '📺', name: '开奖现场'},
          {icon: '📚', name: '资料大全'},
          {icon: '📊', name: '资讯统计'},
          {icon: '🔍', name: '查询助手'},
          {icon: '🎲', name: '幽默猜测'},
          {icon: '🖼️', name: '六合图库'},
          {icon: '🧰', name: '工具宝箱'},
          {icon: '📝', name: '问题反馈'},
        ].map((item, index) => (
          <TouchableOpacity key={index} style={styles.menuItem} onPress={() => handleMenuClick(item.name)}>
            <Text style={styles.menuIcon}>{item.icon}</Text>
            <Text style={styles.menuText}>{item.name}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* 彩图分类 */}
      <View style={styles.pictureTabs}>
        <TouchableOpacity
          style={[styles.pictureTab, {backgroundColor: color === 1 ? '#4caf50' : '#999'}]}
          onPress={() => setColor(1)}
        >
          <Text style={styles.pictureTabIcon}>🖼️</Text>
          <Text style={styles.pictureTabText}>彩图</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.pictureTab, {backgroundColor: color === 2 ? '#666' : '#999'}]}
          onPress={() => setColor(2)}
        >
          <Text style={styles.pictureTabIcon}>📄</Text>
          <Text style={styles.pictureTabText}>黑白</Text>
        </TouchableOpacity>
      </View>
    </>
  );

  const openImageDetail = (item: any) => {
    setSelectedImage(item);
    setModalVisible(true);
  };

  // 开奖现场视频
  const openLiveModal = async () => {
    setShowLiveModal(true);
    setVideoLoading(true);
    setVideoUrl('');
    
    try {
      const res = await fetch(`${API_BASE_URL}/lottery/video?lotteryType=${currentType}`);
      const data = await res.json();
      
      if (data.code === 1 && data.data?.videoUrl) {
        setVideoUrl(data.data.videoUrl);
      } else {
        alert('暂无开奖视频');
      }
    } catch (e) {
      console.error('获取视频失败', e);
      alert('获取视频失败');
    } finally {
      setVideoLoading(false);
    }
  };

  // 搜索图库点击
  const onSearchItemClick = (item: any) => {
    setShowSearchModal(false);
    setSelectedImage(item);
    setModalVisible(true);
  };

  // 数据统计上报
  const trackAction = (action: string, extra: string = '') => {
    fetch(`${API_BASE_URL}/stats/record`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: `action=${action}&lottery_type=${currentType}&extra=${encodeURIComponent(extra)}`
    }).catch(() => {});
  };

  // 处理菜单点击
  const handleMenuClick = (name: string) => {
    switch (name) {
      case '开奖现场':
        trackAction('view_live');
        openLiveModal();
        break;
      case '资料大全':
        trackAction('view_corpus');
        setShowCorpusModal(true);
        break;
      case '资讯统计':
        setShowStatsModal(true);
        break;
      case '查询助手':
        trackAction('view_query');
        setShowQueryModal(true);
        break;
      case '幽默猜测':
        trackAction('view_ymcc');
        setShowYmccModal(true);
        break;
      case '六合图库':
        trackAction('view_gallery');
        setShowSearchModal(true);
        break;
      case '工具宝箱':
        trackAction('view_tool');
        setShowToolModal(true);
        break;
      case '问题反馈':
        trackAction('view_feedback');
        setShowFeedbackModal(true);
        break;
      default:
        alert(`${name} 功能开发中...`);
    }
  };

  const renderPictureItem = ({ item }: any) => (
    <TouchableOpacity style={styles.pictureItem} onPress={() => openImageDetail(item)}>
      {(item.image || item.pictureUrl) ? (
        <ProgressiveImage 
          uri={item.image || item.pictureUrl} 
          style={styles.pictureImage}
        />
      ) : (
        <View style={[styles.pictureImage, styles.imagePlaceholder]}>
          <Text style={{ color: '#ccc', fontSize: 24 }}>🖼️</Text>
        </View>
      )}
      <View style={styles.pictureInfo}>
        <Text style={styles.pictureTitle} numberOfLines={2}>{item.title || item.pictureName}</Text>
      </View>
    </TouchableOpacity>
  );

  // 图库骨架屏
  const renderPictureSkeleton = () => (
    <View style={styles.row}>
      {[1, 2].map((i) => (
        <View key={i} style={styles.pictureItem}>
          <View style={[styles.pictureImage, styles.skeletonImage]} />
          <View style={styles.pictureInfo}>
            <View style={styles.skeletonText} />
          </View>
        </View>
      ))}
    </View>
  );

  // 生成分享图
  const [saving, setSaving] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  // 打开分享弹窗并生成合成图片
  const handleShare = async () => {
    // 统计：点击分享
    trackAction('click_share', selectedImage?.title || '');
    
    setShowShareModal(true);
    setGenerating(true);
    setGeneratedImage(null);
    
    // 等待DOM渲染完成后生成图片
    setTimeout(() => {
      generateShareImage();
    }, 500);
  };

  // 生成合成图片
  const generateShareImage = async () => {
    try {
      const shareContainer = document.getElementById('share-image-source');
      if (!shareContainer) {
        setGenerating(false);
        return;
      }

      // 动态加载 html2canvas
      if (!(window as any).html2canvas) {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
        script.onload = async () => {
          await doGenerateImage(shareContainer);
        };
        script.onerror = () => {
          setGenerating(false);
        };
        document.head.appendChild(script);
      } else {
        await doGenerateImage(shareContainer);
      }
    } catch (err) {
      console.error('生成失败', err);
      setGenerating(false);
    }
  };

  // 执行图片生成
  const doGenerateImage = async (element: HTMLElement) => {
    try {
      const html2canvas = (window as any).html2canvas;
      // 使用更高的缩放比例，确保图片清晰
      const pixelRatio = Math.max(window.devicePixelRatio || 2, 3);
      const canvas = await html2canvas(element, {
        useCORS: true,
        allowTaint: true,
        scale: pixelRatio,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 0,
        removeContainer: true
      });
      const dataUrl = canvas.toDataURL('image/png', 1.0);
      setGeneratedImage(dataUrl);
    } catch (err) {
      console.error('截图失败', err);
    } finally {
      setGenerating(false);
    }
  };

  // 检测是否在移动端
  const isMobile = () => /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);

  // 下载图片（仅桌面端使用）
  const handleDownloadImage = () => {
    if (!generatedImage) return;
    
    // 统计：保存图片
    trackAction('save_image', selectedImage?.title || '');
    
    const link = document.createElement('a');
    const fileName = `${selectedImage?.title || '图库资料'}_${selectedImage?.period || ''}.png`;
    link.download = fileName.replace(/[\/\\:*?"<>|]/g, '_');
    link.href = generatedImage;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    alert('✅ 图片已保存！');
  };

  // 禁用原生图片保存
  const disableContextMenu = (e: any) => {
    e.preventDefault();
    return false;
  };

  // 渲染分享图弹窗
  const renderShareModal = () => (
    <Modal
      visible={showShareModal}
      transparent={true}
      animationType="fade"
      onRequestClose={() => setShowShareModal(false)}
    >
      <View style={styles.shareModalOverlay}>
        {/* 隐藏的原始内容，用于生成图片 */}
        <View style={styles.hiddenSource} nativeID="share-image-source">
          {selectedImage && (
              <Image
                source={{ uri: selectedImage.image || selectedImage.pictureUrl }}
                style={styles.shareImgPic}
                resizeMode="contain"
              />
          )}
          <View style={styles.shareImgFooter}>
            <Image
              source={{ uri: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(shareConfig.qrcode_url)}` }}
              style={styles.shareImgQR}
            />
            <View style={styles.shareImgFooterInfo}>
              <Text style={styles.shareImgSiteName}>{shareConfig.share_title}</Text>
              <Text style={styles.shareImgUrl}>{shareConfig.display_url}</Text>
            </View>
          </View>
        </View>

        <ScrollView style={styles.shareModalScroll} contentContainerStyle={styles.shareModalScrollContent}>
          {/* 提示信息 */}
          <View style={styles.saveImageTipBox}>
            <Text style={styles.saveImageTip}>📱 截图保存下方图片即可分享</Text>
          </View>
          
          {/* 显示生成的合成图片 */}
          {generating ? (
            <View style={styles.generatingContainer}>
              <Text style={styles.generatingText}>⏳ 正在生成分享图...</Text>
            </View>
          ) : generatedImage ? (
            <View style={styles.shareImageWrapper}>
              <Image
                source={{ uri: generatedImage }}
                style={styles.generatedImage}
                resizeMode="contain"
              />
            </View>
          ) : (
            <View style={styles.generatingContainer}>
              <Text style={styles.generatingText}>加载中...</Text>
            </View>
          )}
          
          {/* 按钮区域 */}
          <View style={styles.shareButtonGroup}>
            {/* 桌面端显示下载按钮 */}
            {!isMobile() && (
            <TouchableOpacity 
              style={[styles.saveButton, (!generatedImage || generating) && styles.saveButtonDisabled]} 
              onPress={handleDownloadImage}
              disabled={!generatedImage || generating}
            >
                <Text style={styles.saveButtonText}>💾 保存图片</Text>
            </TouchableOpacity>
            )}
            <TouchableOpacity 
              style={[styles.shareModalClose, isMobile() && { flex: 1 }]} 
              onPress={() => setShowShareModal(false)}
            >
              <Text style={styles.shareModalCloseText}>{isMobile() ? '截图完成，关闭' : '关闭'}</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
  
  const renderImageModal = () => (
    <Modal
      visible={modalVisible}
      transparent={false}
      animationType="slide"
      onRequestClose={() => setModalVisible(false)}
    >
      <SafeAreaView style={styles.detailContainer}>
        {/* 顶部导航 */}
        <View style={styles.detailHeader}>
          <TouchableOpacity style={styles.backBtn} onPress={() => setModalVisible(false)}>
            <Text style={styles.backBtnText}>← 返回</Text>
          </TouchableOpacity>
          <Text style={styles.detailHeaderTitle}>图库详情</Text>
          <View style={styles.backBtn} />
        </View>
        
        {selectedImage && (
          <ScrollView style={styles.detailContent} showsVerticalScrollIndicator={false}>
            {/* 简洁详情区域 - 图片+二维码+网址 */}
            <View style={styles.detailCard}>
              {/* 图片 */}
                <Image
                  source={{ uri: selectedImage.image || selectedImage.pictureUrl }}
                  style={styles.detailImage}
                  resizeMode="contain"
                />
              
              {/* 二维码和网址区域 */}
              <View style={styles.detailFooter}>
                <Image
                  source={{ uri: `https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(shareConfig.qrcode_url)}` }}
                  style={styles.detailQR}
                />
                <View style={styles.detailFooterInfo}>
                  <Text style={styles.detailSiteName}>{shareConfig.share_title}</Text>
                  <Text style={styles.detailUrlText}>{shareConfig.display_url}</Text>
                </View>
              </View>
            </View>
            
            {/* 分享按钮 */}
            <TouchableOpacity style={styles.shareButton} onPress={handleShare}>
              <Text style={styles.shareButtonText}>📤 分享资料</Text>
            </TouchableOpacity>
          </ScrollView>
        )}
      </SafeAreaView>
    </Modal>
  );

  // 开奖现场弹窗 - 播放视频
  const renderLiveModal = () => (
    <Modal
      visible={showLiveModal}
      transparent={false}
      animationType="slide"
      onRequestClose={() => setShowLiveModal(false)}
    >
      <SafeAreaView style={styles.liveContainer}>
        <View style={styles.liveHeader}>
          <TouchableOpacity style={styles.backBtn} onPress={() => setShowLiveModal(false)}>
            <Text style={styles.backBtnText}>← 返回</Text>
          </TouchableOpacity>
          <Text style={styles.liveHeaderTitle}>📺 开奖现场</Text>
          <View style={styles.backBtn} />
        </View>

        <View style={styles.liveContent}>
          {/* 彩种和期数 */}
          <Text style={styles.liveLotteryName}>{LOTTERY_NAMES[currentType]}</Text>
          <Text style={styles.livePeriod}>第 {lotteryInfo?.record?.period || '--'} 期 开奖视频回放</Text>
          
          {/* 视频播放区域 */}
          <View style={styles.videoContainer}>
            {videoLoading ? (
              <View style={styles.videoLoading}>
                <Text style={styles.videoLoadingText}>⏳ 正在加载视频...</Text>
              </View>
            ) : videoUrl ? (
              <video
                src={videoUrl}
                controls
                autoPlay
                playsInline
                style={{ width: '100%', height: '100%', backgroundColor: '#000' }}
              />
            ) : (
              <View style={styles.videoLoading}>
                <Text style={styles.videoLoadingText}>暂无视频数据</Text>
              </View>
            )}
          </View>

          {/* 开奖结果 */}
          {lotteryInfo?.record?.numberList && (
            <View style={styles.liveResultContainer}>
              <Text style={styles.liveResultTitle}>开奖结果</Text>
              <View style={styles.liveResultBalls}>
                {lotteryInfo.record.numberList.map((item: any, index: number) => (
                  <View 
                    key={index} 
                    style={[styles.liveResultBall, {backgroundColor: item.color === 1 ? '#ef4343' : item.color === 2 ? '#3e73fe' : '#07c160'}]}
                  >
                    <Text style={styles.liveResultBallText}>{item.number}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}
        </View>
      </SafeAreaView>
    </Modal>
  );

  // 查询助手弹窗
  // 查询助手弹窗（懒加载）
  const renderQueryModal = () => (
    <Suspense fallback={<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}><ActivityIndicator size="large" color="#3e73fe" /></View>}>
      <QueryModal visible={showQueryModal} onClose={() => setShowQueryModal(false)} />
    </Suspense>
  );

  // 资讯统计弹窗（懒加载）
  const renderStatsModal = () => (
    <Suspense fallback={<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}><ActivityIndicator size="large" color="#3e73fe" /></View>}>
      <StatsModal visible={showStatsModal} onClose={() => setShowStatsModal(false)} />
    </Suspense>
  );

  // 资料大全弹窗（懒加载）
  const renderCorpusModal = () => (
    <Suspense fallback={<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}><ActivityIndicator size="large" color="#8b5cf6" /></View>}>
      <CorpusModal visible={showCorpusModal} onClose={() => setShowCorpusModal(false)} />
    </Suspense>
  );

  // 搜索弹窗（懒加载）
  const renderSearchModal = () => (
    <Suspense fallback={<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}><ActivityIndicator size="large" color="#3e73fe" /></View>}>
      <SearchModal 
        visible={showSearchModal} 
        onClose={() => setShowSearchModal(false)} 
        onItemClick={onSearchItemClick}
      />
    </Suspense>
  );

  // 幽默猜测弹窗
  // 幽默猜测弹窗（懒加载）
  const renderYmccModal = () => (
    <Suspense fallback={<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}><ActivityIndicator size="large" color="#ff6b35" /></View>}>
      <YmccModal visible={showYmccModal} onClose={() => setShowYmccModal(false)} />
    </Suspense>
  );

  // 工具宝箱弹窗渲染（懒加载）
  const renderToolModal = () => (
    <Suspense fallback={<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}><ActivityIndicator size="large" color="#ff9500" /></View>}>
      <ToolModal
        visible={showToolModal}
        onClose={() => setShowToolModal(false)}
      />
    </Suspense>
  );

  // 问题反馈弹窗
  const [feedbackContact, setFeedbackContact] = useState('');
  const [feedbackContent, setFeedbackContent] = useState('');
  const [feedbackImages, setFeedbackImages] = useState<string[]>([]);
  const [feedbackVideo, setFeedbackVideo] = useState<string>('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  // 选择图片
  const handleSelectImages = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e: any) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const newImages: string[] = [];
        Array.from(files).slice(0, 3).forEach((file: any) => {
          const reader = new FileReader();
          reader.onload = (ev) => {
            newImages.push(ev.target?.result as string);
            if (newImages.length === Math.min(files.length, 3)) {
              setFeedbackImages(prev => [...prev, ...newImages].slice(0, 3));
            }
          };
          reader.readAsDataURL(file);
        });
      }
    };
    input.click();
  };

  // 选择视频
  const handleSelectVideo = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    input.onchange = (e: any) => {
      const file = e.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          setFeedbackVideo(ev.target?.result as string);
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const handleFeedbackSubmit = async () => {
    if (!feedbackContent.trim()) {
      alert('请输入反馈内容');
      return;
    }
    setFeedbackSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('contact', feedbackContact);
      formData.append('content', feedbackContent);
      // 添加图片（Base64）
      if (feedbackImages.length > 0) {
        formData.append('images', feedbackImages.join('|||'));
      }
      // 添加视频（Base64）
      if (feedbackVideo) {
        formData.append('video', feedbackVideo);
      }
      const res = await fetch(`${API_BASE_URL}/feedback/submit`, {
        method: 'POST',
        body: formData,
      });
      const json = await res.json();
      if (json.code === 1) {
        alert('提交成功！我们会尽快处理您的反馈');
        setFeedbackContact('');
        setFeedbackContent('');
        setFeedbackImages([]);
        setFeedbackVideo('');
        setShowFeedbackModal(false);
      } else {
        alert(json.msg || '提交失败，请稍后重试');
      }
    } catch (e) {
      alert('网络错误，请稍后重试');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const renderFeedbackModal = () => (
    <Modal
      visible={showFeedbackModal}
      transparent={false}
      animationType="slide"
      onRequestClose={() => setShowFeedbackModal(false)}
    >
      <SafeAreaView style={styles.feedbackContainer}>
        <View style={styles.feedbackHeader}>
          <TouchableOpacity style={styles.backBtn} onPress={() => setShowFeedbackModal(false)}>
            <Text style={styles.backBtnText}>← 返回</Text>
          </TouchableOpacity>
          <Text style={styles.feedbackHeaderTitle}>📝 问题反馈</Text>
          <View style={styles.backBtn} />
        </View>
        <ScrollView style={styles.feedbackContent}>
          <Text style={styles.feedbackTitle}>问题反馈</Text>
          <Text style={styles.feedbackSubtitle}>遇到问题或有建议？请告诉我们</Text>
          
          <View style={styles.feedbackFormGroup}>
            <Text style={styles.feedbackLabel}>联系方式（选填）</Text>
            <TextInput
              style={styles.feedbackInput}
              placeholder="请输入您的手机号或微信"
              value={feedbackContact}
              onChangeText={setFeedbackContact}
              placeholderTextColor="#999"
            />
          </View>
          
          <View style={styles.feedbackFormGroup}>
            <Text style={styles.feedbackLabel}>反馈内容 <Text style={{ color: '#ff4444' }}>*</Text></Text>
            <TextInput
              style={[styles.feedbackInput, styles.feedbackTextarea]}
              placeholder="请详细描述您遇到的问题或建议"
              value={feedbackContent}
              onChangeText={setFeedbackContent}
              multiline
              numberOfLines={6}
              textAlignVertical="top"
              placeholderTextColor="#999"
            />
          </View>
          
          {/* 上传图片 */}
          <View style={styles.feedbackFormGroup}>
            <Text style={styles.feedbackLabel}>上传图片（选填，最多3张）</Text>
            <View style={styles.feedbackUploadRow}>
              {feedbackImages.map((img, index) => (
                <View key={index} style={styles.feedbackImagePreview}>
                  <Image source={{ uri: img }} style={styles.feedbackPreviewImg} />
                  <TouchableOpacity
                    style={styles.feedbackRemoveBtn}
                    onPress={() => setFeedbackImages(prev => prev.filter((_, i) => i !== index))}
                  >
                    <Text style={styles.feedbackRemoveText}>×</Text>
                  </TouchableOpacity>
                </View>
              ))}
              {feedbackImages.length < 3 && (
                <TouchableOpacity style={styles.feedbackUploadBtn} onPress={handleSelectImages}>
                  <Text style={styles.feedbackUploadIcon}>📷</Text>
                  <Text style={styles.feedbackUploadText}>添加图片</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
          
          {/* 上传视频 */}
          <View style={styles.feedbackFormGroup}>
            <Text style={styles.feedbackLabel}>上传视频（选填，最多1个）</Text>
            {feedbackVideo ? (
              <View style={styles.feedbackVideoPreview}>
                <Text style={styles.feedbackVideoName}>🎬 已选择视频</Text>
                <TouchableOpacity onPress={() => setFeedbackVideo('')}>
                  <Text style={styles.feedbackRemoveVideoText}>删除</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity style={styles.feedbackUploadBtn} onPress={handleSelectVideo}>
                <Text style={styles.feedbackUploadIcon}>🎥</Text>
                <Text style={styles.feedbackUploadText}>添加视频</Text>
              </TouchableOpacity>
            )}
          </View>
          
          <TouchableOpacity
            style={[styles.feedbackSubmitBtn, feedbackSubmitting && { backgroundColor: '#ccc' }]}
            onPress={handleFeedbackSubmit}
            disabled={feedbackSubmitting}
          >
            {feedbackSubmitting ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.feedbackSubmitText}>提交反馈</Text>
            )}
          </TouchableOpacity>
          
          <Text style={styles.feedbackTip}>💡 提示：我们会尽快处理您的反馈</Text>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  return (
    <View style={styles.mainContainer}>
      {/* 固定顶部导航栏 */}
      {renderTopNav()}
      
      {/* 可滚动内容区域 */}
      <FlatList
        data={picturesLoading ? [] : pictures}
        renderItem={renderPictureItem}
        keyExtractor={(item, index) => `${item.id}-${index}`}
        numColumns={2}
        ListHeaderComponent={renderHeader}
        onEndReached={() => loadPictures(false)}
        onEndReachedThreshold={0.5}
        ListFooterComponent={
          picturesLoading ? (
            <View>
              {renderPictureSkeleton()}
              {renderPictureSkeleton()}
              {renderPictureSkeleton()}
            </View>
          ) : loading ? (
            <Text style={styles.loading}>加载中...</Text>
          ) : pictures.length === 0 ? (
            <Text style={styles.loading}>暂无数据</Text>
          ) : null
        }
        columnWrapperStyle={pictures.length > 0 ? styles.row : undefined}
        contentContainerStyle={styles.listContainer}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#3e73fe']}
            tintColor="#3e73fe"
            title="下拉刷新"
            titleColor="#999"
          />
        }
        style={styles.flatList}
      />
      {renderImageModal()}
      {renderShareModal()}
      {renderLiveModal()}
      {renderCorpusModal()}
      {renderStatsModal()}
      {renderQueryModal()}
      {renderSearchModal()}
      {renderYmccModal()}
      {renderToolModal()}
      {renderFeedbackModal()}
    </View>
  );
}

const styles = StyleSheet.create({
  mainContainer: { flex: 1, backgroundColor: '#f5f5f5' },
  listContainer: { backgroundColor: '#f5f5f5', paddingBottom: 20 },
  flatList: { flex: 1 },
  topNav: { backgroundColor: COLORS.PRIMARY, padding: 12, flexDirection: 'row', alignItems: 'center' },
  navTab: { paddingHorizontal: 16, paddingVertical: 8, marginRight: 8, borderRadius: 16 },
  navTabActive: { backgroundColor: 'rgba(255,255,255,0.3)' },
  navTabText: { color: 'white', fontSize: 14 },
  searchBtn: { paddingHorizontal: 16 },
  searchText: { color: 'white', fontSize: 14 },
  notice: { backgroundColor: 'white', padding: 12, flexDirection: 'row', alignItems: 'center', marginTop: 8 },
  noticeTag: { backgroundColor: '#ef4343', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4, marginRight: 8 },
  noticeTagText: { color: 'white', fontSize: 12, fontWeight: 'bold' },
  noticeText: { fontSize: 14, color: '#333', flex: 1 },
  lottery: { backgroundColor: 'white', margin: 12, padding: 16, borderRadius: 8, borderWidth: 2, borderColor: '#3e73fe', minHeight: 100 },
  lotteryLoading: { textAlign: 'center', color: '#999', paddingVertical: 30 },
  lotteryIssue: { fontSize: 16, fontWeight: 'bold', marginBottom: 12, textAlign: 'center', color: '#333' },
  lotteryTime: { fontSize: 12, color: '#3e73fe', marginTop: 12, textAlign: 'center' },
  balls: { flexDirection: 'row', justifyContent: 'center', flexWrap: 'wrap', gap: 4 },
  ballItem: { alignItems: 'center', marginHorizontal: 2 },
  ball: { width: 36, height: 36, borderRadius: 18, justifyContent: 'center', alignItems: 'center' },
  ballLatest: { transform: [{ scale: 1.2 }], borderWidth: 3, borderColor: '#ffd700' },
  ballText: { color: 'white', fontSize: 14, fontWeight: 'bold' },
  ballInfo: { fontSize: 10, color: '#666', marginTop: 4 },
  menu: { backgroundColor: 'white', padding: 12, flexDirection: 'row', flexWrap: 'wrap', marginTop: 8 },
  menuItem: { width: '25%', alignItems: 'center', paddingVertical: 12 },
  menuIcon: { fontSize: 32, marginBottom: 4 },
  menuText: { fontSize: 12, color: '#333' },
  pictureTabs: { flexDirection: 'row', padding: 12, gap: 8, marginTop: 8 },
  pictureTab: { flex: 1, padding: 12, borderRadius: 8, alignItems: 'center' },
  pictureTabIcon: { fontSize: 20, marginBottom: 4 },
  pictureTabText: { color: 'white', fontSize: 12, fontWeight: 'bold' },
  row: { gap: 12, paddingHorizontal: 12 },
  pictureItem: { flex: 1, backgroundColor: 'white', borderRadius: 8, overflow: 'hidden', marginBottom: 12 },
  pictureImage: { width: '100%', height: 200 },
  pictureInfo: { padding: 12 },
  pictureTitle: { fontSize: 14, fontWeight: 'bold', marginBottom: 4 },
  pictureMeta: { fontSize: 12, color: '#999' },
  loading: { textAlign: 'center', padding: 20, color: '#999' },
  // 详情页样式
  detailContainer: { flex: 1, backgroundColor: '#f5f5f5' },
  detailHeader: { backgroundColor: COLORS.PRIMARY, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 12 },
  backBtn: { width: 60 },
  backBtnText: { color: 'white', fontSize: 16 },
  detailHeaderTitle: { color: 'white', fontSize: 18, fontWeight: 'bold' },
  detailContent: { flex: 1 },
  detailCard: { backgroundColor: 'white', margin: 12, borderRadius: 12, overflow: 'hidden', borderWidth: 1, borderColor: '#e5e5e5' },
  detailSiteName: { fontSize: 14, fontWeight: 'bold', color: '#333', marginBottom: 2 },
  detailImage: { width: '100%', height: width * 1.2, backgroundColor: 'white' },
  detailFooter: { flexDirection: 'row', padding: 12, borderTopWidth: 1, borderTopColor: '#eee', alignItems: 'center', backgroundColor: '#fafafa' },
  detailQR: { width: 70, height: 70, marginRight: 12 },
  detailFooterInfo: { flex: 1 },
  detailUrlText: { fontSize: 20, color: COLORS.PRIMARY, fontWeight: 'bold' },
  // 分享按钮样式
  shareButton: { backgroundColor: COLORS.PRIMARY, margin: 16, padding: 16, borderRadius: 12, alignItems: 'center' },
  shareButtonText: { color: 'white', fontSize: 18, fontWeight: 'bold' },
  // 分享弹窗样式
  shareModalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.9)' },
  shareModalScroll: { flex: 1 },
  shareModalScrollContent: { padding: 16, alignItems: 'center', paddingTop: 40, paddingBottom: 40 },
  shareModalTip: { fontSize: 14, color: '#fff', marginBottom: 16, textAlign: 'center' },
  hiddenSource: { position: 'absolute', left: -9999, top: -9999, width: 750, backgroundColor: 'white' },
  shareImageWrapper: { width: '100%', maxWidth: 400, alignItems: 'center' },
  generatingContainer: { width: '100%', maxWidth: 400, height: 300, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 12 },
  generatingText: { color: '#fff', fontSize: 16 },
  generatedImage: { width: '100%', maxWidth: 400, height: 600, backgroundColor: 'white' },
  saveImageTipBox: { backgroundColor: 'rgba(255,255,255,0.2)', padding: 12, borderRadius: 8, marginBottom: 12, alignItems: 'center' },
  saveImageTip: { color: '#fff', fontSize: 15, fontWeight: '500' },
  shareImageContainer: { backgroundColor: 'white', borderRadius: 0, overflow: 'hidden', width: '100%' },
  shareImgPic: { width: '100%', height: 800, backgroundColor: '#fff' },
  shareImgFooter: { flexDirection: 'row', padding: 12, backgroundColor: '#f8f8f8', alignItems: 'center' },
  shareImgQR: { width: 80, height: 80, marginRight: 12 },
  shareImgFooterInfo: { flex: 1 },
  shareImgSiteName: { fontSize: 16, fontWeight: 'bold', color: '#333', marginBottom: 2 },
  shareImgUrl: { fontSize: 20, color: COLORS.PRIMARY, fontWeight: 'bold' },
  shareButtonGroup: { width: '100%', maxWidth: 400, marginTop: 20, gap: 12 },
  saveButton: { backgroundColor: '#07c160', paddingVertical: 16, borderRadius: 12, alignItems: 'center' },
  saveButtonDisabled: { backgroundColor: '#999' },
  saveButtonText: { color: 'white', fontSize: 18, fontWeight: 'bold' },
  shareModalClose: { backgroundColor: '#666', paddingVertical: 14, borderRadius: 12, alignItems: 'center' },
  shareModalCloseText: { color: 'white', fontSize: 16 },
  // 开奖现场样式
  liveContainer: { flex: 1, backgroundColor: '#1a1a2e' },
  liveHeader: { backgroundColor: '#16213e', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 12 },
  liveHeaderTitle: { color: '#ffd700', fontSize: 20, fontWeight: 'bold' },
  liveContent: { flex: 1, padding: 16 },
  liveLotteryName: { color: '#ffd700', fontSize: 24, fontWeight: 'bold', textAlign: 'center', marginBottom: 4 },
  livePeriod: { color: '#aaa', fontSize: 14, textAlign: 'center', marginBottom: 16 },
  videoContainer: { width: '100%', height: 250, backgroundColor: '#000', borderRadius: 12, overflow: 'hidden' },
  videoLoading: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  videoLoadingText: { color: '#fff', fontSize: 16 },
  liveResultContainer: { backgroundColor: '#0f3460', borderRadius: 12, padding: 16, marginTop: 16 },
  liveResultTitle: { color: '#fff', fontSize: 16, textAlign: 'center', marginBottom: 12 },
  liveResultBalls: { flexDirection: 'row', justifyContent: 'center', gap: 8 },
  liveResultBall: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  liveResultBallText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
  
  // 骨架屏样式
  skeletonTitle: { 
    width: 180, 
    height: 20, 
    backgroundColor: '#e9e9e9', 
    borderRadius: 4, 
    alignSelf: 'center', 
    marginBottom: 16 
  },
  skeletonBalls: { 
    flexDirection: 'row', 
    justifyContent: 'center', 
    gap: 8 
  },
  skeletonBall: { 
    width: 36, 
    height: 36, 
    borderRadius: 18, 
    backgroundColor: '#e9e9e9' 
  },
  skeletonTime: { 
    width: 150, 
    height: 14, 
    backgroundColor: '#e9e9e9', 
    borderRadius: 4, 
    alignSelf: 'center', 
    marginTop: 16 
  },
  skeletonImage: { 
    backgroundColor: '#e9e9e9' 
  },
  skeletonText: { 
    width: '80%', 
    height: 16, 
    backgroundColor: '#e9e9e9', 
    borderRadius: 4 
  },
  lotteryLoadingText: { 
    textAlign: 'center', 
    color: '#999', 
    paddingVertical: 30 
  },
  animatingTip: {
    textAlign: 'center',
    color: '#ff6b35',
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 8,
  },
  waitingTip: {
    textAlign: 'center',
    color: '#3e73fe',
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 8,
  },
  
  // 倒计时样式
  countdownContainer: { 
    flexDirection: 'row', 
    justifyContent: 'center', 
    alignItems: 'center', 
    marginTop: 12,
    gap: 4 
  },
  countdownLabel: { 
    fontSize: 12, 
    color: '#666', 
    marginRight: 4 
  },
  countdownBox: { 
    flexDirection: 'row', 
    alignItems: 'center' 
  },
  countdownNum: { 
    fontSize: 16, 
    fontWeight: 'bold', 
    color: '#3e73fe', 
    backgroundColor: '#e6f0ff', 
    paddingHorizontal: 6, 
    paddingVertical: 2, 
    borderRadius: 4, 
    minWidth: 28, 
    textAlign: 'center' 
  },
  countdownUnit: { 
    fontSize: 11, 
    color: '#666', 
    marginLeft: 2, 
    marginRight: 4 
  },
  
  // 图片渐进加载样式
  imagePlaceholder: { 
    justifyContent: 'center', 
    alignItems: 'center', 
    backgroundColor: '#f0f0f0' 
  },
  imagePlaceholderIcon: { 
    opacity: 0.5 
  },
  
  // 问题反馈弹窗样式
  feedbackContainer: { flex: 1, backgroundColor: '#f5f5f5' },
  feedbackHeader: { backgroundColor: COLORS.PRIMARY, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 12 },
  feedbackHeaderTitle: { color: '#fff', fontSize: 18, fontWeight: 'bold' },
  feedbackContent: { flex: 1, padding: 20 },
  feedbackTitle: { fontSize: 24, fontWeight: 'bold', color: '#333', marginBottom: 8 },
  feedbackSubtitle: { fontSize: 14, color: '#666', marginBottom: 30 },
  feedbackFormGroup: { marginBottom: 20 },
  feedbackLabel: { fontSize: 16, fontWeight: '500', color: '#333', marginBottom: 8 },
  feedbackInput: { backgroundColor: '#fff', borderRadius: 8, padding: 12, fontSize: 15, color: '#333', borderWidth: 1, borderColor: '#e0e0e0' },
  feedbackTextarea: { minHeight: 120, paddingTop: 12 },
  feedbackSubmitBtn: { backgroundColor: '#3e73fe', borderRadius: 8, padding: 15, alignItems: 'center', marginTop: 10 },
  feedbackSubmitText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
  feedbackTip: { fontSize: 12, color: '#999', textAlign: 'center', marginTop: 15 },
  feedbackUploadRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  feedbackUploadBtn: { width: 80, height: 80, backgroundColor: '#fff', borderRadius: 8, borderWidth: 1, borderColor: '#e0e0e0', borderStyle: 'dashed', justifyContent: 'center', alignItems: 'center' },
  feedbackUploadIcon: { fontSize: 24, marginBottom: 4 },
  feedbackUploadText: { fontSize: 11, color: '#999' },
  feedbackImagePreview: { width: 80, height: 80, borderRadius: 8, overflow: 'hidden', position: 'relative' },
  feedbackPreviewImg: { width: '100%', height: '100%' },
  feedbackRemoveBtn: { position: 'absolute', top: 2, right: 2, width: 20, height: 20, backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  feedbackRemoveText: { color: '#fff', fontSize: 14, fontWeight: 'bold' },
  feedbackVideoPreview: { backgroundColor: '#fff', borderRadius: 8, padding: 12, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', borderWidth: 1, borderColor: '#e0e0e0' },
  feedbackVideoName: { fontSize: 14, color: '#333' },
  feedbackRemoveVideoText: { color: '#ef4444', fontSize: 14 },
});
